import { Group, Menu } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { trackEvent } from '@repo/common/analytics';
import { integrationList } from '@repo/common/constants/integration/integrations.ts';
import { Breadcrumbs, Button, Icon, Title } from '@repo/foundations';
import { width } from '@repo/theme/primitives';
import { filter } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router';
import {
	NavBarBreadcrumbWrapper,
	NavBarRightSideWrapper,
	NavBarWrapper,
} from '.';
import {
	queryClient,
	useAuthUser,
	useCreateDatabuilderJob,
	useIntegration,
	useIntegrationSpec,
	useSuspenseFeatureAccess,
	useUpdateDatabuilderJob,
	useUpdateIntegration,
} from '../../../api';
import {
	forceFetchJobs,
	usePollDatabuilderJobList,
} from '../../../api/hooks/databuilderJob/usePollDatabuilderJob';
import {
	incidentsQueryKeyFactory,
	monitorsQueryKeyFactory,
} from '../../../api/hooks/monitoring/constants.ts';
import { DatabuilderJobType, SyncType } from '../../../interfaces';
import { useV2CheckIAMPermission } from '../../../utils/authorization/roles.ts';
import { useFeatureFlags } from '../../../utils/featureFlags';
import AddIntegrationModal from '../../Integration/AddIntegrationModal';
import { useStyles as useAddIntegrationModalStyles } from '../../Integration/AddIntegrationModal/AddIntegrationModal.styles';
import { NEW_INTEGRATION_MODAL_ID } from '../../Integration/AddIntegrationModal/constants';
import {
	getIdentityTokenIfOidc,
	shouldCancelSyncForOidc,
} from '../../Integration/identityTokenHelpers';
import { IntegrationToggle } from '../../Integration/IntegrationToggle.tsx';
import { openModal } from '../../ModalManager';

interface SyncButtonProps {
	isPolling: boolean;
	integrationSpec: {
		value: {
			disablePullMetadata?: boolean;
			supportsPushMetadata?: boolean;
		};
	} | null;
	onCancelSync: () => void;
	onPullSync: () => void;
	onPushSync: () => void;
}

function SyncButton({
	isPolling,
	integrationSpec,
	onCancelSync,
	onPullSync,
	onPushSync,
}: SyncButtonProps) {
	const { secodaCoreAccess } = useSuspenseFeatureAccess();

	// Check if only one sync option is available (no menu needed)
	const showPullMetadata = !integrationSpec?.value.disablePullMetadata;
	const showPushMetadata =
		integrationSpec?.value.supportsPushMetadata && !!secodaCoreAccess;
	const onlyPullAvailable = showPullMetadata && !showPushMetadata;
	const shouldUseDirectButton = !isPolling && onlyPullAvailable;

	if (shouldUseDirectButton) {
		return (
			<Button variant="primary" leftIconName="arrowDown" onClick={onPullSync}>
				Pull metadata
			</Button>
		);
	}

	// Otherwise use the menu
	return (
		<Menu>
			<Menu.Target>
				<Button
					variant="primary"
					leftIconName={isPolling ? 'refresh' : undefined}
					leftIconAnimation="spinCounterClockwise"
					rightIconName="chevronDown"
				>
					{isPolling ? 'Syncing' : 'Run sync'}
				</Button>
			</Menu.Target>
			<Menu.Dropdown>
				{isPolling && (
					<Menu.Item icon={<Icon name="playerStop" />} onClick={onCancelSync}>
						Cancel sync
					</Menu.Item>
				)}
				{!isPolling && showPullMetadata && (
					<Menu.Item icon={<Icon name="arrowDown" />} onClick={onPullSync}>
						Pull metadata
					</Menu.Item>
				)}
				{!isPolling && showPushMetadata && (
					<Menu.Item icon={<Icon name="arrowUp" />} onClick={onPushSync}>
						Push metadata
					</Menu.Item>
				)}
			</Menu.Dropdown>
		</Menu>
	);
}

function IntegrationPageNavBar() {
	const { classes: newIntegrationModalClasses } =
		useAddIntegrationModalStyles();
	const { nativeSnowflake, extensionsInIntegrationsPage } = useFeatureFlags();

	const hasCreatePermissions = useV2CheckIAMPermission({
		v2Permission: 'Integrations.Create',
		defaultResult: false,
	});
	const hasManagePermissions = useV2CheckIAMPermission({
		v2Permission: 'Integrations.Manage',
		defaultResult: false,
	});

	const location = useLocation();

	const { isAdminUser, user, workspace } = useAuthUser();
	const { id } = useParams();

	const { data: integration } = useIntegration({
		id: id!,
		options: {
			enabled: !!id,
		},
	});
	const { mutateAsync: updateIntegration } = useUpdateIntegration({});
	const integrationSpec = useIntegrationSpec(id || '');

	const { mutate: runSync } = useCreateDatabuilderJob({
		options: {
			onSuccess: forceFetchJobs,
			onError: () => {
				showNotification({
					title: 'Error running sync',
					message: 'Please try again',
					color: 'red',
				});
			},
		},
	});
	const { mutate: updateJob } = useUpdateDatabuilderJob({
		options: {
			onSuccess: forceFetchJobs,
		},
	});
	const { polling: pullPolling, databuilderJobs: pullJobs } =
		usePollDatabuilderJobList({
			integrationId: id || '',
			type: SyncType.PULL,
			enabled: Boolean(id) && (hasManagePermissions || isAdminUser),
		});

	const { polling: pushPolling, databuilderJobs: pushJobs } =
		usePollDatabuilderJobList({
			integrationId: id || '',
			type: SyncType.PUSH,
			enabled: Boolean(id) && (hasManagePermissions || isAdminUser),
		});

	const isIntegrationsOrExtensionsPage =
		location.pathname === '/integrations' ||
		location.pathname === '/integrations/overview' ||
		location.pathname === '/integrations/extensions';

	const isExtensionsPage = location.pathname === '/integrations/extensions';

	const handleNavigate = useCallback(() => {
		openModal({
			modalId: NEW_INTEGRATION_MODAL_ID,
			title: isExtensionsPage ? (
				<Title size="md">Connect Extension</Title>
			) : (
				<Title size="md">Connect Integration</Title>
			),
			children: (
				<AddIntegrationModal
					modalType={isExtensionsPage ? 'extension' : 'integration'}
					integrationTypes={integrationList
						.filter((i) => (isExtensionsPage ? i.isExtension : !i.isExtension))
						.filter((i) => {
							if (i.type === 'native_snowflake') {
								return nativeSnowflake;
							}
							return true;
						})
						.map((i) => i.type)}
				/>
			),
			size: width.xxl,
			radius: 'lg',
			classNames: {
				header: newIntegrationModalClasses.header,
				body: newIntegrationModalClasses.body,
			},
		});
	}, [newIntegrationModalClasses, nativeSnowflake, isExtensionsPage]);

	const toggleEnabled = useCallback(
		(enabled: boolean) => {
			updateIntegration({
				data: {
					id: integration!.id,
					enabled,
				},
			});
		},
		[integration, updateIntegration]
	);

	const handlePullSync = useCallback(async () => {
		if (integration && !pullPolling) {
			const identityToken = await getIdentityTokenIfOidc({
				integration,
			});

			const { shouldCancel } = shouldCancelSyncForOidc(identityToken, {
				integration,
			});

			if (shouldCancel) {
				return;
			}

			await runSync({
				data: {
					integration_id: id,
					type: DatabuilderJobType.METADATA,
					...(identityToken && { identity_token: identityToken }),
				},
			});
			trackEvent('integrations/run_extraction/click', {}, user, workspace!);
			// ENG-10149 Invalidate the queries for monitors and incidents
			await queryClient.invalidateQueries({
				queryKey: monitorsQueryKeyFactory.allLists(),
			});
			await queryClient.invalidateQueries({
				queryKey: incidentsQueryKeyFactory.allLists(),
			});
		}
	}, [integration, id, pullPolling, runSync, user, workspace, integrationSpec]);

	const handlePushSync = useCallback(async () => {
		if (integration && !pushPolling) {
			await runSync({
				data: {
					integration_id: id,
					type: DatabuilderJobType.PUSH,
				},
			});
			trackEvent(
				'integration/sync/push',
				{
					integration_type: integration.type,
				},
				user,
				workspace!
			);
		}
	}, [integration, id, pushPolling, runSync, user, workspace]);

	const handleCancelSync = useCallback(() => {
		const idsToCancel = filter(
			[...(pullJobs ?? []), ...(pushJobs ?? [])],
			(job) =>
				!job.failed && !job.completed && !job.terminated && !job.timed_out
		).map((job) => job.id);

		idsToCancel.forEach((id) => {
			updateJob({
				data: {
					id: id,
					terminated: true,
				},
			});
		});
	}, [pullJobs, pushJobs, updateJob]);

	const isPolling = pullPolling || pushPolling;

	const breadcrumbs = useMemo(() => {
		if (!integration) {
			return [{ title: 'Integrations', href: '/integrations' }];
		}

		if (integrationList.find((i) => i.type === integration.type)?.isExtension) {
			return [
				{
					title: 'Extensions',
					href: extensionsInIntegrationsPage
						? '/integrations/extensions'
						: '/settings/extensions',
				},
				{ title: integration.name, href: `/extensions/${integration.id}` },
			];
		}

		return [
			{ title: 'Integrations', href: '/integrations' },
			{ title: integration.name, href: `/integrations/${integration.id}` },
		];
	}, [integration, extensionsInIntegrationsPage]);

	if (isIntegrationsOrExtensionsPage) {
		return (
			<NavBarWrapper>
				<NavBarBreadcrumbWrapper>
					<Title
						order={1}
						size="sm"
						data-testid="integrations-page-nav-bar-title"
					>
						{integrationList.find((i) => i.type === integration?.type)
							?.isExtension
							? 'Extensions'
							: 'Integrations'}
					</Title>
				</NavBarBreadcrumbWrapper>
				<NavBarRightSideWrapper>
					{(hasCreatePermissions || isAdminUser) && (
						<Button
							variant="primary"
							onClick={handleNavigate}
							data-testid="new-integrations-button"
						>
							{isExtensionsPage ? 'Connect extension' : 'Connect integration'}
						</Button>
					)}
				</NavBarRightSideWrapper>
			</NavBarWrapper>
		);
	}

	return (
		<NavBarWrapper>
			<NavBarBreadcrumbWrapper>
				<Breadcrumbs crumbs={breadcrumbs} />
			</NavBarBreadcrumbWrapper>
			{integration &&
				(hasManagePermissions || isAdminUser) &&
				!['slack', 'ms_teams', 'pagerduty'].includes(integration.type) && (
					<NavBarRightSideWrapper>
						<Group spacing="sm">
							{integration && (
								<IntegrationToggle
									enabled={integration.enabled}
									onChange={toggleEnabled}
								/>
							)}
							<SyncButton
								isPolling={isPolling}
								integrationSpec={integrationSpec}
								onCancelSync={handleCancelSync}
								onPullSync={handlePullSync}
								onPushSync={handlePushSync}
							/>
						</Group>
					</NavBarRightSideWrapper>
				)}
		</NavBarWrapper>
	);
}

export default IntegrationPageNavBar;
